/**
 * The canvas: viewport (zoom / pan), the artboard, and the pointer state
 * machine that drives select / move / resize / rotate / marquee / draw.
 *
 * All gesture bookkeeping lives in `design-canvas-gesture.ts` and all the maths
 * in `design-geometry.utils.ts`; this component is the wiring between them, the
 * two stores and the DOM.
 *
 * Pointer events (not mouse events) throughout, with pointer capture, so a drag
 * that leaves the window still ends cleanly.
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  AfterViewInit,
  computed,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

import { DesignCanvasStore } from '../../state/design-canvas.store';
import { DesignDocumentStore } from '../../state/design-document.store';
import {
  DEFAULT_ICON,
  DEFAULT_ICON_SIZE,
  MIN_NODE_SIZE,
  SCREEN_FRAME_HEIGHT,
} from '../../design-builder.constants';
import { ICON_BY_NAME } from '../icon-library/design-icon-catalogue';
import { defaultIconAction } from '../../utils/design-icon-actions.utils';
import {
  DESIGN_ICON_DRAG_TYPE,
  DESIGN_PALETTE_DRAG_TYPE,
  type AlignmentGuide,
  type DesignNode,
  type PaletteItem,
  type Point,
  type Rect,
} from '../../design-builder.types';
import {
  fitRectInside,
  normalizeRect,
  rectsIntersect,
  resizeRect,
  roundGeometry,
  snapRect,
  snapToGrid,
} from '../../utils/design-geometry.utils';
import {
  createNodeFromPalette,
  findContainerAt,
  locateNode,
} from '../../utils/design-node.utils';
import { gridBackground, layoutGridOverlay } from '../../utils/design-style.utils';
import {
  type ActiveGesture,
  angleFrom,
  collectSnapTargets,
  createGesture,
  passedSlop,
} from './design-canvas-gesture';
import { DesignNodeViewComponent, type NodePointerEvent } from './design-node-view.component';
import {
  DesignSelectionBoxComponent,
  type HandlePointerEvent,
} from './design-selection-box.component';

@Component({
  selector: 'app-design-canvas',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DesignNodeViewComponent, DesignSelectionBoxComponent],
  templateUrl: './design-canvas.component.html',
  styleUrl: './design-canvas.component.scss',
})
export class DesignCanvasComponent implements AfterViewInit, OnDestroy {
  private readonly doc = inject(DesignDocumentStore);
  private readonly view = inject(DesignCanvasStore);
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Palette items, so a drop can resolve its key back to a node factory. */
  readonly palette = input<readonly PaletteItem[]>([]);
  /** Read-only preview — hides chrome and disables every gesture. */
  readonly preview = input<boolean>(false);
  /** Data mode: unbound components render as placeholders, not sample charts. */
  readonly hideSample = input<boolean>(false);

  readonly requestInspector = output<string>();

  private readonly viewportRef = viewChild.required<ElementRef<HTMLElement>>('viewportEl');

  private gesture: ActiveGesture | null = null;
  private resizeObserver?: ResizeObserver;

  // ── Exposed state ──────────────────────────────────────────────────────────
  readonly nodes = this.doc.nodes;
  readonly canvas = this.doc.canvas;
  readonly selectedIds = this.doc.selectedIds;
  readonly guides = this.view.guides;
  readonly marquee = this.view.marquee;
  readonly dropTargetId = this.view.dropTargetId;
  readonly editingTextId = this.view.editingTextId;
  readonly viewport = this.view.viewport;
  readonly isPanMode = this.view.isPanMode;

  /** Live widgets are suspended only while actually DRAGGING — not on a plain
   *  click, which would needlessly destroy and recreate every chart. */
  readonly suspended = this.view.dragging;

  /**
   * The artboard is a COORDINATE SPACE, not a page — it draws no background of
   * its own. The white page you see is the Screen frame; everything outside it
   * is workspace. Painting the artboard as well put a second, slightly
   * different rectangle behind the frame.
   */
  readonly artboardStyles = computed<Record<string, string>>(() => {
    const canvas = this.canvas();
    return {
      width: `${canvas.width}px`,
      height: `${canvas.height}px`,
      transform: this.view.canvasTransform(),
      'transform-origin': '0 0',
    };
  });

  /**
   * Workspace background + grid, drawn across the WHOLE viewport and offset by
   * the pan, so the grid fills the empty space instead of stopping at the
   * artboard edge.
   */
  readonly workspaceStyles = computed<Record<string, string>>(() => {
    const canvas = this.canvas();
    const { zoom, panX, panY } = this.viewport();
    return {
      background: canvas.background,
      // Off while the layout grid is up, which belongs on the pages only.
      ...(canvas.grid.visible && !this.view.layoutGrid()
        ? gridBackground(canvas.grid.size, zoom, panX, panY)
        : {}),
    };
  });

  /**
   * The layout grid, one per root Frame (the white Screen pages) and clipped to
   * it — the workspace around the pages stays clear. Each grid starts at its
   * frame's top-left, which is the origin children snap against.
   */
  readonly layoutGrids = computed<Array<{ id: string; styles: Record<string, string> }>>(() => {
    if (!this.view.layoutGrid()) return [];
    this.doc.revision();
    const { zoom, panX, panY } = this.viewport();
    const size = this.canvas().grid.size;
    return this.nodes()
      .filter((node) => node.type === 'FRAME' && node.visible)
      .map((frame) => ({
        id: frame.id,
        styles: {
          left: `${frame.x * zoom + panX}px`,
          top: `${frame.y * zoom + panY}px`,
          width: `${frame.width * zoom}px`,
          height: `${frame.height * zoom}px`,
          ...layoutGridOverlay(size, zoom),
        },
      }));
  });

  /**
   * The edge of every page (root frame), drawn in screen space so it stays a
   * crisp 1px at any zoom. A page whose fill matches the workspace was
   * otherwise invisible once zoomed out — the design seemed to float in grey.
   */
  readonly frameOutlines = computed<Array<{ id: string; styles: Record<string, string> }>>(() => {
    this.doc.revision();
    const { zoom, panX, panY } = this.viewport();
    return this.nodes()
      .filter((node) => node.type === 'FRAME' && node.visible)
      .map((frame) => ({
        id: frame.id,
        styles: {
          left: `${frame.x * zoom + panX}px`,
          top: `${frame.y * zoom + panY}px`,
          width: `${frame.width * zoom}px`,
          height: `${frame.height * zoom}px`,
        },
      }));
  });

  /**
   * One screenful of the published page, marked across each screen that has
   * outgrown it: above the first fold is what a viewer sees on open, below it
   * is what they scroll to.
   */
  readonly foldLines = computed<Array<{ id: string; label: string; styles: Record<string, string> }>>(() => {
    this.doc.revision();
    const { zoom, panX, panY } = this.viewport();
    const folds: Array<{ id: string; label: string; styles: Record<string, string> }> = [];
    for (const frame of this.nodes()) {
      if (frame.type !== 'FRAME' || !frame.visible) continue;
      for (let at = SCREEN_FRAME_HEIGHT; at < frame.height; at += SCREEN_FRAME_HEIGHT) {
        folds.push({
          id: `${frame.id}_${at}`,
          label: `Fold · ${at}px`,
          styles: {
            left: `${frame.x * zoom + panX}px`,
            top: `${(frame.y + at) * zoom + panY}px`,
            width: `${frame.width * zoom}px`,
          },
        });
      }
    }
    return folds;
  });

  readonly selectionBounds = computed<Rect | null>(() => {
    // Recomputes whenever the document changes, not just the selection.
    this.doc.revision();
    return this.doc.selectionBounds();
  });

  readonly selectionRotation = computed(() => this.doc.selectedNode()?.rotation ?? 0);
  readonly multiSelect = computed(() => this.selectedIds().size > 1);
  readonly selectedIsGroup = computed(() => this.doc.selectedNode()?.type === 'GROUP');

  /** The screen is the page itself: selectable for its settings, never movable. */
  readonly selectedIsScreen = computed(() => {
    const node = this.doc.selectedNode();
    return !!node && this.doc.isScreen(node.id);
  });
  readonly interacting = this.view.isInteracting;

  /** Marquee converted to screen px for the overlay. */
  readonly marqueeStyles = computed<Record<string, string>>(() => {
    const rect = this.marquee();
    if (!rect?.active) return { display: 'none' } as Record<string, string>;
    const { zoom, panX, panY } = this.viewport();
    return {
      left: `${rect.x * zoom + panX}px`,
      top: `${rect.y * zoom + panY}px`,
      width: `${rect.width * zoom}px`,
      height: `${rect.height * zoom}px`,
    };
  });

  constructor() {
    // Keep the store's idea of the artboard size in sync, so pan clamping and
    // fit-to-width react to a canvas resize from the inspector.
    effect(() => {
      const canvas = this.canvas();
      this.view.setContentSize(canvas.width, canvas.height);
    });
  }

  ngAfterViewInit(): void {
    const element = this.viewportRef().nativeElement;
    this.resizeObserver = new ResizeObserver(() => this.syncViewport(element));
    this.resizeObserver.observe(element);
    this.syncViewport(element);

    // Open on the whole design, fitted to the pane. Zoom is a VIEW concern, so
    // this is the only place the pane size influences anything.
    queueMicrotask(() => this.view.fitToWidth());
  }

  /**
   * Track the viewport size for zoom and pan clamping only.
   *
   * It must NOT resize the artboard. The board is part of the DESIGN, while
   * this element's size is editor chrome — collapsing a side panel was
   * resizing the screen while the layers inside kept their px positions, so
   * the design no longer fitted it. Opening a panel changes how much you SEE,
   * never what you designed.
   */
  private syncViewport(element: HTMLElement): void {
    this.view.setViewportSize(element.clientWidth, element.clientHeight);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  zoomToFit(): void {
    const canvas = this.canvas();
    this.view.fitTo({ x: 0, y: 0, width: canvas.width, height: canvas.height });
  }

  // ── Wheel: ctrl/⌘ zooms, plain wheel pans ─────────────────────────────────

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey) {
      // Trackpad pinch arrives as a ctrl+wheel with small deltas; the exponent
      // keeps both it and a mouse wheel feeling proportional.
      const factor = Math.pow(0.99, event.deltaY);
      this.view.zoomBy(factor, this.localPoint(event));
      return;
    }
    if (event.shiftKey) this.view.panBy(-event.deltaY, 0);
    else this.view.panBy(-event.deltaX, -event.deltaY);
  }

  // ── Pointer down ───────────────────────────────────────────────────────────

  /** Empty canvas: pan, draw a new node, or start a marquee. */
  onCanvasPointerDown(event: PointerEvent): void {
    if (this.preview()) return;
    this.view.setEditingText(null);

    const screen = this.localPoint(event);
    const docPoint = this.view.toDocument(screen);

    if (this.isPanMode() || event.button === 1) {
      this.gesture = createGesture('pan', screen, docPoint, []);
      this.gesture.startPan = { x: this.view.panX(), y: this.view.panY() };
      this.view.setGesture('pan');
      this.capture(event);
      return;
    }

    const tool = this.view.tool();
    if (tool !== 'select' && tool !== 'hand') {
      this.gesture = createGesture('draw', screen, docPoint, []);
      this.view.setGesture('draw');
      this.capture(event);
      return;
    }

    if (!event.shiftKey) this.doc.clearSelection();
    this.gesture = createGesture('marquee', screen, docPoint, []);
    this.view.setGesture('marquee');
    this.view.setMarquee({ ...docPoint, width: 0, height: 0, active: true });
    this.capture(event);
  }

  /** A node was pressed: update the selection, then start a move. */
  onNodePointerDown({ node, event }: NodePointerEvent): void {
    if (this.preview() || this.isPanMode()) return;

    // A group draws nothing, so the press landed on one of its members. The
    // group is what gets selected — that is what makes it one thing to drag.
    // Ctrl/⌘ reaches past it to the single member inside.
    const target = event.ctrlKey || event.metaKey
      ? node
      : this.doc.outermostGroupOf(node.id) ?? node;

    if (event.shiftKey) this.doc.toggleSelected(target.id);
    else if (!this.doc.isSelected(target.id)) this.doc.select(target.id);

    this.requestInspector.emit(target.id);

    // A press on a widget's own control (dropdown, slider, input, upload…)
    // operates the control: starting a move would capture the pointer and
    // cancel the mouse events the control opens / changes on. A button / link
    // only needs its click, so dragging it (past the slop) still moves the widget.
    const onControl = isWidgetControlTarget(event.target);
    if (onControl && !isClickControlTarget(event.target)) return;

    const moving = this.movableSelection();
    if (!moving.length) return;

    const screen = this.localPoint(event);
    this.gesture = createGesture(
      'move',
      screen,
      this.view.toDocument(screen),
      moving,
      this.snapTargetsFor(moving),
    );
    if (onControl) {
      this.gesture.deferredCapture = true;
      return;
    }
    this.view.setGesture('move');
    this.capture(event);
  }

  /** A press on a button turned into a drag: take the pointer now, and keep the click from pressing it. */
  private beginDeferredMove(gesture: ActiveGesture, event: PointerEvent): void {
    gesture.deferredCapture = false;
    (event.currentTarget as Element | null)?.setPointerCapture?.(event.pointerId);
    this.view.setGesture('move');
    const guard = (click: Event) => {
      click.stopPropagation();
      click.preventDefault();
    };
    gesture.clickGuard = guard;
    window.addEventListener('click', guard, { capture: true, once: true });
  }

  onNodeDoubleClick({ node }: NodePointerEvent): void {
    if (this.preview()) return;
    if (node.type === 'TEXT') this.view.setEditingText(node.id);
  }

  onTextCommit({ node, value }: { node: DesignNode; value: string }): void {
    this.view.setEditingText(null);
    if (value === node.text?.value) return;
    this.doc.updateNode(node.id, { text: { ...node.text!, value } }, 'Edit text');
  }

  onResizeStart({ handle, event }: HandlePointerEvent): void {
    const node = this.doc.selectedNode();
    if (!node || node.locked || this.doc.isScreen(node.id)) return;
    const screen = this.localPoint(event);
    // A group resizes by scaling its members, so they are snapshotted too.
    const members = node.memberIds?.length ? this.doc.memberLeaves(node) : [];
    this.gesture = createGesture(
      'resize',
      screen,
      this.view.toDocument(screen),
      [node, ...members],
      this.snapTargetsFor([node]),
    );
    this.gesture.handle = handle;
    this.view.setGesture('resize');
    this.capture(event);
  }

  groupSelection(): void {
    this.doc.groupSelected();
  }

  onRotateStart(event: PointerEvent): void {
    const node = this.doc.selectedNode();
    const bounds = this.selectionBounds();
    // A group has no box of its own to turn, and rotating its members
    // individually is not what the handle promises, so it does not rotate.
    if (!node || !bounds || node.locked || this.doc.isScreen(node.id) || node.memberIds?.length) return;

    const screen = this.localPoint(event);
    const docPoint = this.view.toDocument(screen);
    const center = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };

    this.gesture = createGesture('rotate', screen, docPoint, [node]);
    this.gesture.center = center;
    this.gesture.startRotation = node.rotation;
    this.gesture.startAngle = angleFrom(center, docPoint);
    this.view.setGesture('rotate');
    this.capture(event);
  }

  // ── Pointer move / up ──────────────────────────────────────────────────────

  onPointerMove(event: PointerEvent): void {
    const gesture = this.gesture;
    if (!gesture) return;

    const screen = this.localPoint(event);
    if (!passedSlop(gesture, screen)) return;
    if (gesture.deferredCapture) this.beginDeferredMove(gesture, event);
    gesture.moved = true;
    // Only NOW is this a real drag, so only now swap live widgets for
    // placeholders — a click that never moved leaves them mounted.
    this.view.setDragging(true);

    const docPoint = this.view.toDocument(screen);
    const dx = docPoint.x - gesture.originDoc.x;
    const dy = docPoint.y - gesture.originDoc.y;

    switch (gesture.kind) {
      case 'pan': {
        const start = gesture.startPan!;
        this.view.panTo(
          start.x + (screen.x - gesture.originScreen.x),
          start.y + (screen.y - gesture.originScreen.y),
        );
        break;
      }
      case 'move':
        this.applyMove(gesture, dx, dy);
        break;
      case 'resize':
        this.applyResize(gesture, dx, dy, event);
        break;
      case 'rotate':
        this.applyRotate(gesture, docPoint, event.shiftKey);
        break;
      case 'marquee':
        this.applyMarquee(gesture, docPoint);
        break;
      case 'draw':
        this.view.setMarquee({ ...normalizeRect(gesture.originDoc, docPoint), active: true });
        break;
    }
  }

  onPointerUp(event: PointerEvent): void {
    const gesture = this.gesture;
    this.gesture = null;
    if (!gesture) return;

    (event.target as Element).releasePointerCapture?.(event.pointerId);
    // The click (if any) follows this pointerup at once; never let the guard eat a later one.
    const guard = gesture.clickGuard;
    if (guard) setTimeout(() => window.removeEventListener('click', guard, { capture: true }));

    if (gesture.kind === 'marquee') this.commitMarquee(gesture, event);
    if (gesture.kind === 'draw') this.commitDraw(gesture, event);

    this.view.setMarquee(null);
    this.view.setGesture('idle');
    this.view.clearGuides();

    // Charts sized themselves against the pre-gesture box; nudge them to re-fit.
    if (gesture.moved && (gesture.kind === 'resize' || gesture.kind === 'move')) {
      this.reflowCharts();
    }
  }

  // ── Gesture application ────────────────────────────────────────────────────

  private applyMove(gesture: ActiveGesture, dx: number, dy: number): void {
    const canvas = this.canvas();
    const guides: AlignmentGuide[] = [];

    // Snap the FIRST node and apply the resulting correction to the rest, so a
    // multi-selection keeps its internal spacing.
    let correctionX = 0;
    let correctionY = 0;

    gesture.nodeIds.forEach((id, index) => {
      const start = gesture.startRects.get(id);
      if (!start) return;
      const moved: Rect = { ...start, x: start.x + dx, y: start.y + dy };

      if (index === 0) {
        const snapped = snapRect(moved, gesture.snapTargets, this.view.zoom(), canvas.grid);
        correctionX = snapped.rect.x - moved.x;
        correctionY = snapped.rect.y - moved.y;
        guides.push(...snapped.guides);
      }

      this.doc.setNodeRect(
        id,
        { x: roundGeometry(moved.x + correctionX), y: roundGeometry(moved.y + correctionY) },
        'Move',
      );
    });

    this.view.setGuides(guides);
  }

  private applyResize(gesture: ActiveGesture, dx: number, dy: number, event: PointerEvent): void {
    const id = gesture.nodeIds[0];
    const start = gesture.startRects.get(id);
    if (!start || !gesture.handle) return;

    const resized = resizeRect(start, gesture.handle, dx, dy, {
      keepRatio: event.shiftKey,
      fromCenter: event.altKey,
    });
    // Edges are snapped here rather than by snapRect, which only MOVES a rect:
    // with grid snap it would shift the box instead of landing the dragged edge.
    const grid = this.canvas().grid;
    const snapped = snapRect(resized, gesture.snapTargets, this.view.zoom(), { ...grid, snap: false });
    // Shift (keep ratio) and Alt (from centre) move edges in lockstep, which
    // per-edge grid snapping would break — those resizes snap to guides only.
    const rect = grid.snap && !event.shiftKey && !event.altKey
      ? snapResizedEdges(snapped.rect, resized, gesture.handle, grid.size, snapped.guides)
      : snapped.rect;

    this.view.setGuides(snapped.guides);
    const startNode = gesture.startNodes.get(id);
    if (startNode?.memberIds?.length) this.doc.resizeGroup(id, rect, gesture.startNodes, 'Resize');
    else this.doc.resizeNode(id, rect, startNode, 'Resize');
  }

  private applyRotate(gesture: ActiveGesture, docPoint: Point, snap: boolean): void {
    const id = gesture.nodeIds[0];
    const delta = angleFrom(gesture.center!, docPoint) - gesture.startAngle!;
    let rotation = (gesture.startRotation ?? 0) + delta;
    if (snap) rotation = Math.round(rotation / 15) * 15;
    this.doc.updateNode(id, { rotation: Math.round(rotation) }, 'Rotate');
  }

  private applyMarquee(gesture: ActiveGesture, docPoint: Point): void {
    this.view.setMarquee({ ...normalizeRect(gesture.originDoc, docPoint), active: true });
  }

  /** Select every root node the marquee touched. */
  private commitMarquee(gesture: ActiveGesture, event: PointerEvent): void {
    if (!gesture.moved) return;
    const rect = normalizeRect(gesture.originDoc, this.view.toDocument(this.localPoint(event)));
    // Candidates are the layers INSIDE each screen (in page coordinates) plus
    // loose page-level layers — not the screens themselves. Testing only the
    // top level selected the whole screen instead of the cards boxed in it.
    const candidates: Array<{ node: DesignNode; box: Rect }> = [];
    for (const root of this.nodes()) {
      if (root.type === 'FRAME' && this.doc.isScreen(root.id)) {
        for (const child of root.children ?? []) {
          candidates.push({ node: child, box: { x: root.x + child.x, y: root.y + child.y, width: child.width, height: child.height } });
        }
      } else {
        candidates.push({ node: root, box: root });
      }
    }
    const encloses = (box: Rect) =>
      box.x <= rect.x && box.y <= rect.y && box.x + box.width >= rect.x + rect.width && box.y + box.height >= rect.y + rect.height;
    const hits = [...new Set(candidates
      // A panel that fully surrounds the box is the background being dragged
      // INSIDE, not something being boxed. Groups draw nothing: their members
      // are tested, and resolve to the group below.
      .filter(({ node, box }) => node.visible && !node.locked && !node.memberIds?.length && rectsIntersect(rect, box) && !encloses(box))
      .map(({ node }) => this.doc.outermostGroupOf(node.id)?.id ?? node.id))];
    if (!hits.length) return;
    this.doc.selectMany(event.shiftKey ? [...this.selectedIds(), ...hits] : hits);
  }

  /** Drop a node sized by the drag (or at its default size for a click). */
  private commitDraw(gesture: ActiveGesture, event: PointerEvent): void {
    const tool = this.view.tool();
    const item = this.palette().find((candidate) => candidate.key === tool);
    if (!item) {
      this.view.resetTool();
      return;
    }

    const end = this.view.toDocument(this.localPoint(event));
    const rect = normalizeRect(gesture.originDoc, end);
    const node = createNodeFromPalette(item, rect);
    if (gesture.moved) {
      node.width = Math.max(8, roundGeometry(rect.width));
      node.height = Math.max(item.nodeType === 'LINE' ? 1 : 8, roundGeometry(rect.height));
    }

    this.insertAt(node, rect);
    this.view.resetTool();
  }

  // ── Palette drag & drop ────────────────────────────────────────────────────

  onDragOver(event: DragEvent): void {
    const types = event.dataTransfer?.types;
    const accepted =
      !!types &&
      (types.includes(DESIGN_PALETTE_DRAG_TYPE) || types.includes(DESIGN_ICON_DRAG_TYPE));
    if (this.preview() || !accepted) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';

    const point = this.view.toDocument(this.localPoint(event));
    this.view.setDropTarget(findContainerAt(this.nodes(), point, null)?.id ?? null);
  }

  onDragLeave(): void {
    this.view.setDropTarget(null);
  }

  onDrop(event: DragEvent): void {
    if (this.preview()) return;
    this.view.setDropTarget(null);

    const iconName = event.dataTransfer?.getData(DESIGN_ICON_DRAG_TYPE);
    if (iconName) {
      event.preventDefault();
      this.dropIcon(iconName, event);
      return;
    }

    const raw = event.dataTransfer?.getData(DESIGN_PALETTE_DRAG_TYPE);
    if (!raw) return;
    event.preventDefault();

    const item = this.palette().find((candidate) => candidate.key === raw);
    if (!item) return;

    const point = this.view.toDocument(this.localPoint(event));
    // Drop centred on the cursor — it feels wrong to drop from the corner.
    const at = { x: point.x - item.defaultWidth / 2, y: point.y - item.defaultHeight / 2 };
    const node = createNodeFromPalette(item, at);

    this.insertAt(node, at);
  }

  /**
   * Insert a node under whatever container it was dropped INSIDE, rebasing its
   * coordinates into that container's space.
   *
   * A FRAME is the exception: a root frame IS a screen, and a screen nested in
   * another screen is not a thing, so frames always land at the root.
   */
  private insertAt(node: DesignNode, at: Point): void {
    if (node.type === 'FRAME') {
      this.doc.addNode(node, null);
      return;
    }

    const centre = { x: at.x + node.width / 2, y: at.y + node.height / 2 };
    const hit = findContainerAt(this.nodes(), centre, node.id);
    if (!hit) {
      this.doc.addNode(node, null);
      return;
    }

    // x/y are parent-relative, so subtract the container's absolute origin.
    let box = { x: roundGeometry(node.x - hit.absoluteX), y: roundGeometry(node.y - hit.absoluteY), width: node.width, height: node.height };
    // A box inside the page - a popup screen, a card frame - fits what is dropped in it (shrunk
    // to its size, kept within its edges). A page screen grows down with its content instead.
    const container = locateNode(this.nodes(), hit.id)?.node;
    if (container && !this.doc.isScreen(container.id)) box = fitRectInside(box, container);
    this.doc.addNode({ ...node, ...box }, hit.id);
  }

  /** Insert an icon node for a name dragged out of the icon library. */
  private dropIcon(name: string, event: DragEvent): void {
    this.insertIcon(name, ICON_BY_NAME.get(name)?.label ?? name, this.view.toDocument(this.localPoint(event)));
  }

  /** Insert an icon in the middle of the visible area — used when the library
   *  is clicked rather than dragged. */
  insertIconAtCenter(name: string, label: string): void {
    const element = this.viewportRef().nativeElement;
    const center = this.view.toDocument({
      x: element.clientWidth / 2,
      y: element.clientHeight / 2,
    });
    this.insertIcon(name, label, center);
  }

  /** Shared icon insertion, centred on `center` (document space). */
  private insertIcon(name: string, label: string, center: Point): void {
    const size = DEFAULT_ICON_SIZE;
    const at = { x: center.x - size / 2, y: center.y - size / 2 };

    const node = createNodeFromPalette(
      {
        key: `icon:${name}`,
        label,
        icon: 'star',
        group: 'design',
        nodeType: 'ICON',
        defaultWidth: size,
        defaultHeight: size,
      },
      at,
    );
    node.icon = { ...DEFAULT_ICON, name, action: defaultIconAction(name) };
    node.name = label;

    this.insertAt(node, at);
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  /** A snap guide in screen px. Guides span only the nodes that produced them,
   *  so they read as an alignment hint rather than a full-canvas ruler. */
  guideStyles(guide: AlignmentGuide): Record<string, string> {
    const { zoom, panX, panY } = this.viewport();
    const at = guide.at * zoom + (guide.axis === 'x' ? panX : panY);
    const from = guide.from * zoom + (guide.axis === 'x' ? panY : panX);
    const length = (guide.to - guide.from) * zoom;

    return guide.axis === 'x'
      ? { left: `${at}px`, top: `${from}px`, height: `${length}px` }
      : { top: `${at}px`, left: `${from}px`, width: `${length}px` };
  }

  trackById(_: number, node: { id: string }): string {
    return node.id;
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Pointer position relative to the viewport element, in screen px. */
  private localPoint(event: { clientX: number; clientY: number }): Point {
    const rect = this.viewportRef().nativeElement.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private capture(event: PointerEvent): void {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  /**
   * Selected nodes that are actually movable — never the screen, and a group
   * stands in for its members, since it owns no geometry of its own.
   */
  private movableSelection(): DesignNode[] {
    const moving = new Map<string, DesignNode>();
    for (const node of this.doc.selectedNodes()) {
      if (node.locked || this.doc.isScreen(node.id)) continue;
      for (const leaf of this.doc.memberLeaves(node)) {
        if (!leaf.locked) moving.set(leaf.id, leaf);
      }
    }
    return [...moving.values()];
  }

  /** Sibling + parent rects the moving nodes may snap to. */
  private snapTargetsFor(moving: DesignNode[]): Rect[] {
    const nodes = this.nodes();
    const first = moving[0];
    const location = locateNode(nodes, first.id);
    const movingIds = new Set(moving.map((node) => node.id));
    return collectSnapTargets(
      location?.siblings ?? nodes,
      movingIds,
      location?.parent ?? null,
      this.canvas(),
    );
  }

  /** ECharts widgets measure on resize only — the same nudge the Layout Builder uses. */
  private reflowCharts(): void {
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  // ── Space-to-pan ───────────────────────────────────────────────────────────

  @HostListener('window:keydown.space', ['$event'])
  onSpaceDown(event: Event): void {
    // Not while typing — space belongs to the input.
    if (isTypingTarget(event.target)) return;
    event.preventDefault();
    this.view.setSpaceHeld(true);
  }

  @HostListener('window:keyup.space')
  onSpaceUp(): void {
    this.view.setSpaceHeld(false);
  }
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || element.isContentEditable;
}

/** Interactive controls rendered INSIDE a widget, which must receive the pointer. */
const WIDGET_CONTROL_SELECTOR = [
  'input', 'select', 'textarea', 'button', 'a[href]', '[contenteditable="true"]',
  '.ng-select', 'mat-slider', '.mat-mdc-slider', '.mat-mdc-form-field',
].join(', ');

function isWidgetControlTarget(target: EventTarget | null): boolean {
  const element = target instanceof Element ? target : null;
  return !!element?.closest('.dsg-widget-live') && !!element.closest(WIDGET_CONTROL_SELECTOR);
}

/** A control that only needs its click (a button, a link): it can still be dragged. */
function isClickControlTarget(target: EventTarget | null): boolean {
  const element = target instanceof Element ? target : null;
  const control = element?.closest(WIDGET_CONTROL_SELECTOR);
  return !!control && control.matches('button, a[href]') && !control.closest('.ng-select, mat-slider, .mat-mdc-slider, .mat-mdc-form-field');
}

/**
 * Grid-snap only the edges the handle moves, on any axis an alignment guide
 * has not already claimed. The opposite edge stays put, so a resize never
 * slides the box.
 */
function snapResizedEdges(
  rect: Rect,
  resized: Rect,
  handle: string,
  size: number,
  guides: AlignmentGuide[],
): Rect {
  let { x, y, width, height } = rect;
  if (!guides.some((guide) => guide.axis === 'x')) {
    x = resized.x;
    width = resized.width;
    if (handle.includes('w')) {
      const right = x + width;
      const snappedX = snapToGrid(x, size);
      if (right - snappedX >= MIN_NODE_SIZE) {
        x = snappedX;
        width = right - x;
      }
    } else if (handle.includes('e')) {
      width = Math.max(MIN_NODE_SIZE, snapToGrid(x + width, size) - x);
    }
  }
  if (!guides.some((guide) => guide.axis === 'y')) {
    y = resized.y;
    height = resized.height;
    if (handle.includes('n')) {
      const bottom = y + height;
      const snappedY = snapToGrid(y, size);
      if (bottom - snappedY >= MIN_NODE_SIZE) {
        y = snappedY;
        height = bottom - y;
      }
    } else if (handle.includes('s')) {
      height = Math.max(MIN_NODE_SIZE, snapToGrid(y + height, size) - y);
    }
  }
  return {
    ...rect,
    x: roundGeometry(x),
    y: roundGeometry(y),
    width: roundGeometry(width),
    height: roundGeometry(height),
  };
}
